import { DEFAULT_DECIMAL_PLACES } from '@shared/models/decimal-places';

export class GlobalSettings {
  modulesActivation: ModulesActivation = new ModulesActivation();
  preventMultipleTabs: boolean = false;
  amountsDecimalPlaces: number = DEFAULT_DECIMAL_PLACES.amount;
  quantityDecimalPlaces: number = DEFAULT_DECIMAL_PLACES.quantity;
  desktopApp: DesktopAppSettings = new DesktopAppSettings();

  static clone(value: any) {
    const settings = Object.assign(new GlobalSettings(), value);
    settings.desktopApp = Object.assign(new DesktopAppSettings(), value?.desktopApp);
    return settings;
  }
}

export class DesktopAppSettings {
  dataReplication: boolean = false;

  /** How many tasks are enabled — drives the "Not in use" / "Enabled · N tasks" header status */
  static countEnabledTasks(tasks: Partial<DesktopAppSettings> | null | undefined): number {
    return Object.values(tasks ?? {}).filter(enabled => enabled === true).length;
  }
}

export class ModulesActivation {
  purchasing: boolean = false;
  pointOfSale: boolean = false;
  invoicing: boolean = false;
  manufacturing: boolean = false;
  fixedAssets: boolean = false;
  analytics: boolean = false;
  subscriptions: boolean = false;
  moduleTransactions: ModuleTransactions = new ModuleTransactions();
  moduleVersionConfig: ModuleVersionConfig = new ModuleVersionConfig();
}

export class ModuleTransactions {
  invoicingTransactions?: InvoicingTransactions;
}

export class ModuleVersionConfig {
  posVersion: ModuleVersionSettings = new ModuleVersionSettings();
}

export class ModuleVersionSettings {
  version: ModuleVersion = ModuleVersion.V3_0;
  usersLevelVersion?: UserVersionConfig[];
}

export class UserVersionConfig {
  user!: string;
  version: ModuleVersion.V3_0 | ModuleVersion.V2_5 = ModuleVersion.V3_0;
}

export class OrganizationProfile {
  organizationName!: string;
  country!: number;
  taxRegistrationNumber?: string;
  commercialRegistrationNumber?: string;
  currency!: number;
  industry?: string;
  address?: any;
  businessDomain?: BusinessDomain;

  static clone(value: any) {
    const settings = new OrganizationProfile();
    Object.assign(settings, value);
    return settings;
  }
}

export class BusinessDomain {
  sectors: string[] = [];
  productLines: string[] = [];
  salesChannels: string[] = [];
  businessEngagement: string[] = [];
}

export class OrdersSetting {
  useSalesTax: boolean = true;
  fulfillmentMode: FulfillmentMode = FulfillmentMode.Automatic;
  automaticFulfillmentCondition: AutomaticFulfillmentCondition = AutomaticFulfillmentCondition.WhenOrderPaidAndDelivered;
  salesRecognition: SalesRecognitionSetting = SalesRecognitionSetting.Fulfilled;
  availableToSellFormula: AvailableToSellFormula = AvailableToSellFormula.OnHand_Refund_Orders_PreOrders
  paymentTerminal: boolean = false;

  static clone(value: any) {
    const settings = new OrdersSetting();
    Object.assign(settings, value);
    return settings;
  }
}

export class PurchaseInvoicesSetting {
  compoundDiscount: boolean = false;

  static clone(value: any) {
    const settings = new PurchaseInvoicesSetting();
    Object.assign(settings, value);
    return settings;
  }
}

export class SalesInvoicesSetting {
  defaultTaxId!: string;
  productPrices: InvoicingProductPrices = InvoicingProductPrices.Inclusive;
  einvoice_ksa_configuration?: EInvoiceKsaConfiguration;
  submissionMode: SubmissionMode = SubmissionMode.Later;

  static clone(value: any) {
    const settings = new SalesInvoicesSetting();
    Object.assign(settings, value);
    return settings;
  }
}

export interface EInvoiceKsaConfiguration {
  firstConnectionDate: Date
  connected: boolean;
  environment: string;
  invoiceType: string;
  otp: string;
  commonName: string;
  taxRegistrationNumber: string;
  branchName: string;
  organizationName: string;
  countryName: string;
  address: any;
  industry: string;
  serialNumber: string;
  CSID_PrivateKey: string;
  CSID_CSR: string;
  CSID_RequestID: number;
  CSID_TokenType: string;
  CSID_BinarySecurityToken: string;
  CSID_Secret: string;
  CSID_DispositionMessage: string;
  commercialRegistrationNumber: string;
  SubmissionData_Supplier_StreetName: string;
  SubmissionData_Supplier_CityName: string;
  SubmissionData_Supplier_CitySubdivisionName: string;
  SubmissionData_Supplier_BuildingNumber: string;
  SubmissionData_Supplier_PostalZone: string;
}

export class ProductsSetting {
  nameUnique: boolean = false;
  skuRequired: boolean = true;
  skuUnique: boolean = true;
  partNumberRequired: boolean = true;
  partNumberUnique: boolean = true;
  brandRequired: boolean = false;
  brandUnique: boolean = false;

  static clone(value: any) {
    const settings = new ProductsSetting();
    Object.assign(settings, value);
    return settings;
  }
}

export enum InvoicingTransactions {
  All = 1,
  SalesInvoices = 2,
  PurchaseInvoices = 3
}

export enum InvoicingProductPrices {
  Inclusive = "Inclusive",
  Exclusive = "Exclusive"
}

export enum SubmissionMode {
  Instant = "Instant",
  Later = "Later"
}

export enum SalesRecognitionSetting {
  Fulfilled = 'fulfilled',
  Delivered = 'delivered'
}

export enum FulfillmentMode {
  Manual = 1,
  Automatic = 2
}

export enum AutomaticFulfillmentCondition {
  WhenOrderPaidAndDelivered = 1,
  WhenOrderDelivered = 2
}

export enum AvailableToSellFormula {
  OnHand_Refund_Orders_PreOrders = 'OnHand_Refund_Orders_PreOrders',
  OnHand_Refund_Orders = 'OnHand_Refund_Orders'
}

export enum ModuleVersion {
  V3_0 = 1,
  V2_5 = 2,
  UserLevel = 3
}

export enum SubscriptionTermUnits {
  Months = 'months',
  Years = 'years'
}

export enum SubscriptionRenewalAlignment {
  StartDay = 'own',
  MonthStartShortFirst = 'first',
  MonthStartLongFirst = 'roundup'
}

export enum SubscriptionRenewalArrival {
  Draft = 'draft',
  Active = 'active'
}

export enum SubscriptionDowngradePolicy {
  Prevented = 'prevent',
  Allowed = 'allow'
}

export enum SubscriptionDeferRevenue {
  WhenNeeded = 'crossing',
  Always = 'always',
  Never = 'never'
}

export enum SubscriptionRevenueDivision {
  ByDays = 'days',
  PeriodRate = 'periodrate',
  Equal = 'evenwhole'
}

export enum SubscriptionAccountingPeriod {
  CalendarMonth = 'month'
}

export class SubscriptionsSetting {
  termUnits: SubscriptionTermUnits[] = [SubscriptionTermUnits.Months];
  renewalAlignment: SubscriptionRenewalAlignment = SubscriptionRenewalAlignment.StartDay;
  renewalArrival: SubscriptionRenewalArrival = SubscriptionRenewalArrival.Draft;
  defaultTaxId: string = '';
  downgrades: SubscriptionDowngradePolicy = SubscriptionDowngradePolicy.Prevented;
  deferRevenue: SubscriptionDeferRevenue = SubscriptionDeferRevenue.WhenNeeded;
  revenueDivision: SubscriptionRevenueDivision = SubscriptionRevenueDivision.ByDays;
  accountingPeriod: SubscriptionAccountingPeriod = SubscriptionAccountingPeriod.CalendarMonth;

  static clone(value: any) {
    const settings = new SubscriptionsSetting();
    Object.assign(settings, value);
    // At least one term unit must stay on (spec: the last unit cannot be switched off).
    if (!Array.isArray(settings.termUnits) || settings.termUnits.length === 0) {
      settings.termUnits = [SubscriptionTermUnits.Months];
    }
    return settings;
  }
}

export const SUBSCRIPTION_DEFAULT_CHURN_REASONS: string[] = [
  'Too expensive',
  'Missing features',
  'Switched to a competitor',
  'No longer needed'
];

export class SubscriptionsChurnReasons {
  reasons: string[] = [...SUBSCRIPTION_DEFAULT_CHURN_REASONS];

  static clone(value: any) {
    const settings = new SubscriptionsChurnReasons();
    Object.assign(settings, value);
    if (!Array.isArray(settings.reasons)) {
      settings.reasons = [...SUBSCRIPTION_DEFAULT_CHURN_REASONS];
    }
    return settings;
  }
}

// Which rate wins when both a manual table rate and a live rate exist for a date.
export type SubscriptionFxPriority = 'table' | 'live';

// A dated manual rate: `rate` is the system-currency value of one unit of the
// foreign currency, effective from `on` (inclusive) until the next row starts.
export interface SubscriptionFxRateRow {
  on: string;   // 'YYYY-MM-DD'
  rate: number;
}

export class SubscriptionsFxRates {
  // Legacy single default, kept so settings stored before per-currency choices still read.
  priority: SubscriptionFxPriority = 'table';
  // Per-currency "Suggest first", keyed by foreign-currency ISO code. Each currency
  // chooses whether the manual table or the live rate is suggested first.
  priorities: { [code: string]: SubscriptionFxPriority } = {};
  // keyed by foreign-currency ISO code; rows kept sorted ascending by `on`
  rates: { [code: string]: SubscriptionFxRateRow[] } = {};

  static clone(value: any) {
    const settings = new SubscriptionsFxRates();
    Object.assign(settings, value);
    if (settings.priority !== 'live') { settings.priority = 'table'; }
    if (!settings.priorities || typeof settings.priorities !== 'object') { settings.priorities = {}; }
    if (!settings.rates || typeof settings.rates !== 'object') { settings.rates = {}; }
    return settings;
  }
}

// The foreign currencies the organization deals in, by ISO code. The system currency is
// always in use and is not stored here. Removing one keeps its past transactions and their
// rates; its manual rates stay, locked, until it is added back.
export class SubscriptionsCurrenciesUsed {
  codes: string[] = [];

  static clone(value: any) {
    const settings = new SubscriptionsCurrenciesUsed();
    const codes = Array.isArray(value?.codes) ? value.codes : (Array.isArray(value) ? value : []);
    settings.codes = codes
      .map((c: any) => String(c ?? '').toUpperCase().trim())
      .filter((c: string) => !!c);
    return settings;
  }
}

// The company-wide Subscriptions start date — every customer's opening balance is dated on it.
export class SubscriptionsOpeningBalances {
  subscriptionsStartDate?: string;   // 'YYYY-MM-DD'

  static clone(value: any) {
    const settings = new SubscriptionsOpeningBalances();
    Object.assign(settings, value);
    return settings;
  }
}

export type SubscriptionCustomerType = 'Consumer' | 'Business';

export interface SubscriptionFieldDef {
  key: string;
  label: string;                        // i18n key suffix
  type: SubscriptionCustomerType | null; // null = applies to every customer type
  lockReq?: boolean;                    // Required cell locked on
  lockUq?: boolean;                     // Unique cell locked on
  lockUqOff?: boolean;                  // Unique cell locked off (e.g. product Brand)
  uniqDefault?: boolean;                // Unique defaults on when unset (e.g. product Name)
  why?: string;                         // i18n key suffix for the lock tooltip
  whyUq?: string;                       // i18n key suffix for a Unique-specific tooltip
}

export interface SubscriptionFieldConfig {
  visible: boolean;
  required: boolean;
  unique: boolean;
}

export type SubscriptionFieldsConfig = { [key: string]: SubscriptionFieldConfig };

export const SUBSCRIPTION_CUSTOMER_FIELDS: SubscriptionFieldDef[] = [
  { key: 'name', label: 'name', type: null, lockReq: true, lockUq: true, why: 'nameLock' },
  { key: 'mobile', label: 'mobile', type: null },
  { key: 'phone', label: 'phone', type: null },
  { key: 'email', label: 'email', type: null },
  { key: 'nid', label: 'nid', type: 'Consumer' },
  { key: 'taxid', label: 'taxid', type: 'Business' },
  { key: 'taxname', label: 'taxname', type: 'Business' },
  { key: 'crn', label: 'crn', type: 'Business' }
];

// Normalize a saved config against the field defs, enforcing the coherence rules:
// locked cells are always on, and a hidden field cannot be required or unique.
export function normalizeSubscriptionFields(defs: SubscriptionFieldDef[], saved?: SubscriptionFieldsConfig): SubscriptionFieldsConfig {
  const out: SubscriptionFieldsConfig = {};
  defs.forEach(f => {
    const s = (saved && saved[f.key]) || {} as SubscriptionFieldConfig;
    const visible = f.lockReq || f.lockUq ? true : (s.visible === undefined ? true : !!s.visible);
    out[f.key] = {
      visible,
      required: f.lockReq ? true : (visible && !!s.required),
      unique: f.lockUq ? true : (visible && (s.unique === undefined ? !!f.uniqDefault : !!s.unique))
    };
  });
  return out;
}

export class SubscriptionsCustomerFields {
  fields: SubscriptionFieldsConfig = normalizeSubscriptionFields(SUBSCRIPTION_CUSTOMER_FIELDS);

  static clone(value: any) {
    const settings = new SubscriptionsCustomerFields();
    settings.fields = normalizeSubscriptionFields(SUBSCRIPTION_CUSTOMER_FIELDS, value && value.fields);
    return settings;
  }
}

// Product fields have no Visible column (a product's visibility is its Usage switches)
// and no type groups. Name's Required is locked on; Brand's Unique is locked off.
export const SUBSCRIPTION_PRODUCT_FIELDS: SubscriptionFieldDef[] = [
  { key: 'name', label: 'name', type: null, lockReq: true, uniqDefault: true, why: 'nameLock' }
];

export interface SubscriptionProductFieldConfig {
  required: boolean;
  unique: boolean;
}

export type SubscriptionProductFieldsConfig = { [key: string]: SubscriptionProductFieldConfig };

export function normalizeSubscriptionProductFields(saved?: SubscriptionProductFieldsConfig): SubscriptionProductFieldsConfig {
  const out: SubscriptionProductFieldsConfig = {};
  SUBSCRIPTION_PRODUCT_FIELDS.forEach(f => {
    const s = (saved && saved[f.key]) || {} as SubscriptionProductFieldConfig;
    out[f.key] = {
      required: f.lockReq ? true : !!s.required,
      unique: f.lockUq ? true : f.lockUqOff ? false : (s.unique === undefined ? !!f.uniqDefault : !!s.unique)
    };
  });
  return out;
}

export class SubscriptionsProductFields {
  fields: SubscriptionProductFieldsConfig = normalizeSubscriptionProductFields();

  static clone(value: any) {
    const settings = new SubscriptionsProductFields();
    settings.fields = normalizeSubscriptionProductFields(value && value.fields);
    return settings;
  }
}

// --- Subscription fields configuration (Header + Lines) ---

export const SUBSCRIPTION_HEADER_FIELDS: SubscriptionFieldDef[] = [
  { key: 'Subscription', label: 'name', type: null, lockReq: true, lockUq: true, why: 'subNameLock' },
  { key: 'Customer', label: 'customer', type: null, lockReq: true, why: 'customerLock' },
  { key: 'Workspace', label: 'workspace', type: null, uniqDefault: true },
  { key: 'Reference', label: 'reference', type: null, uniqDefault: true },
  { key: 'Contract Date', label: 'contractDate', type: null },
  { key: 'Service Start Date', label: 'serviceStartDate', type: null },
  { key: 'Sales Person', label: 'salesPerson', type: null }
];

export interface SubscriptionLineSourceOption {
  value: string;
  label: string;   // i18n key suffix
}

export interface SubscriptionLineFieldDef {
  field: string;
  label: string;                // i18n key suffix
  fixed?: string;               // i18n key suffix when the source is stated, not chosen
  sourceKey?: 'price' | 'tax';  // which stored source this field uses
  def?: string;
  opts?: SubscriptionLineSourceOption[];
}

export const SUBSCRIPTION_LINE_FIELDS: SubscriptionLineFieldDef[] = [
  { field: 'Quantity', label: 'quantity', fixed: 'typedOnLine' },
  {
    field: 'Unit price', label: 'unitPrice', sourceKey: 'price', def: 'master',
    opts: [{ value: 'master', label: 'planPriceOnly' }, { value: 'open', label: 'canBeTypedOver' }]
  },
  { field: 'Discount', label: 'discount', fixed: 'typedOnLine' },
  {
    field: 'Tax', label: 'tax', sourceKey: 'tax', def: 'open',
    opts: [{ value: 'master', label: 'productDefaultOnly' }, { value: 'open', label: 'canBeTypedOver' }]
  }
];

export interface SubscriptionEditorsMap {
  [key: string]: string[];
}

export interface SubscriptionLineSources {
  price: string;
  tax: string;
}

// Empty list = anyone may edit; a list with people in it restricts to those people.
export function normalizeSubscriptionEditors(keys: string[], saved?: SubscriptionEditorsMap): SubscriptionEditorsMap {
  const out: SubscriptionEditorsMap = {};
  keys.forEach(k => {
    const v = (saved && saved[k]) || [];
    const list = (Array.isArray(v) ? v : []).map(x => String(x).trim()).filter(Boolean);
    if (list.length) {
      out[k] = list;
    }
  });
  return out;
}

export interface SubscriptionRollupDef {
  key: string;
  name: string;   // i18n key suffix
  reads: string;  // i18n key suffix
}

// The five built-in "Cycles" rollups (read the whole chain). Product rollups are
// added per subscribable product once the module has its own products.
export const SUBSCRIPTION_CYCLE_ROLLUPS: SubscriptionRollupDef[] = [
  { key: 'sub:terms', name: 'cycles', reads: 'cyclesReads' },
  { key: 'sub:renewals', name: 'renewals', reads: 'renewalsReads' },
  { key: 'sub:upgrades', name: 'upgrades', reads: 'upgradesReads' },
  { key: 'sub:downgrades', name: 'downgrades', reads: 'downgradesReads' },
  { key: 'sub:invoiced', name: 'invoiced', reads: 'invoicedReads' }
];

export const SUBSCRIPTION_ROLLUP_MAX = 30;

export type SubscriptionRollupsMap = { [key: string]: boolean };

export function normalizeSubscriptionRollups(saved?: SubscriptionRollupsMap): SubscriptionRollupsMap {
  const out: SubscriptionRollupsMap = {};
  // Cycles rollups ship visible (spec: what ships is all of them).
  SUBSCRIPTION_CYCLE_ROLLUPS.forEach(r => {
    const s = saved ? saved[r.key] : undefined;
    out[r.key] = s === undefined ? true : !!s;
  });
  // Preserve any product-rollup keys the tenant already stored.
  if (saved) {
    Object.keys(saved).forEach(k => {
      if (out[k] === undefined) {
        out[k] = !!saved[k];
      }
    });
  }
  return out;
}

export class SubscriptionsSubscriptionFields {
  headerFields: SubscriptionFieldsConfig = normalizeSubscriptionFields(SUBSCRIPTION_HEADER_FIELDS);
  headerEditors: SubscriptionEditorsMap = {};
  lineSources: SubscriptionLineSources = { price: 'master', tax: 'open' };
  lineEditors: SubscriptionEditorsMap = {};
  rollups: SubscriptionRollupsMap = normalizeSubscriptionRollups();

  static clone(value: any) {
    const settings = new SubscriptionsSubscriptionFields();
    const v = value || {};
    settings.headerFields = normalizeSubscriptionFields(SUBSCRIPTION_HEADER_FIELDS, v.headerFields);
    settings.headerEditors = normalizeSubscriptionEditors(SUBSCRIPTION_HEADER_FIELDS.map(f => f.key), v.headerEditors);
    const price = v.lineSources && v.lineSources.price;
    const tax = v.lineSources && v.lineSources.tax;
    settings.lineSources = {
      price: (price === 'master' || price === 'open') ? price : 'master',
      tax: (tax === 'master' || tax === 'open') ? tax : 'open'
    };
    settings.lineEditors = normalizeSubscriptionEditors(SUBSCRIPTION_LINE_FIELDS.map(f => f.field), v.lineEditors);
    settings.rollups = normalizeSubscriptionRollups(v.rollups);
    return settings;
  }
}

// --- Approval workflows (a chain of steps a cycle climbs before it may be invoiced) ---

export interface SubscriptionWorkflowOption {
  key: string;
  label: string;   // i18n key suffix
}

export interface SubscriptionWorkflowGoal {
  key: string;
  entity: string;  // i18n key suffix — what the chain runs on (Cycles)
  label: string;   // i18n key suffix
}

// One goal shipped: a cycle climbs the chain before it may be invoiced.
export const SUBSCRIPTION_WF_GOALS: SubscriptionWorkflowGoal[] = [
  { key: 'invoice', entity: 'cycles', label: 'invoice' }
];

// When a step applies.
export const SUBSCRIPTION_WF_CONDITIONS: SubscriptionWorkflowOption[] = [
  { key: 'always', label: 'always' },
  { key: 'renewal', label: 'renewal' },
  { key: 'change', label: 'change' },
  { key: 'discount', label: 'discount' }
];

// The fields a step may lock once it is passed (locks accumulate down the chain).
export const SUBSCRIPTION_WF_LOCKABLE: SubscriptionWorkflowOption[] = [
  { key: 'Plan', label: 'plan' },
  { key: 'Currency', label: 'currency' },
  { key: 'Cycle period', label: 'cyclePeriod' },
  { key: 'Renewal', label: 'renewal' },
  { key: 'Quantity', label: 'quantity' },
  { key: 'Unit price', label: 'unitPrice' },
  { key: 'Discount', label: 'discount' },
  { key: 'Tax', label: 'tax' }
];

export interface SubscriptionWorkflowStep {
  name: string;
  when: string;        // condition key
  approvers: string[]; // user emails; empty = anyone
  locks: string[];     // field keys locked once this step is passed
}

export interface SubscriptionWorkflow {
  id: string;
  name: string;
  goal: string;        // goal key
  active: boolean;
  steps: SubscriptionWorkflowStep[];
}

export function cloneSubscriptionWorkflows(value: any): SubscriptionWorkflow[] {
  const conditionKeys = SUBSCRIPTION_WF_CONDITIONS.map(c => c.key);
  const lockKeys = SUBSCRIPTION_WF_LOCKABLE.map(f => f.key);
  const list = Array.isArray(value) ? value : [];
  const workflows: SubscriptionWorkflow[] = list.map((w: any) => ({
    id: String(w.id || ''),
    name: String(w.name || ''),
    goal: SUBSCRIPTION_WF_GOALS.some(g => g.key === w.goal) ? String(w.goal) : SUBSCRIPTION_WF_GOALS[0].key,
    active: !!w.active,
    steps: Array.isArray(w.steps) ? w.steps.map((s: any) => ({
      name: String(s.name || ''),
      when: conditionKeys.indexOf(s.when) >= 0 ? String(s.when) : 'always',
      approvers: Array.isArray(s.approvers) ? s.approvers.map((a: any) => String(a)).filter(Boolean) : [],
      locks: Array.isArray(s.locks) ? s.locks.map((f: any) => String(f)).filter((f: string) => lockKeys.indexOf(f) >= 0) : []
    })) : []
  }));
  // Only one workflow per goal may be active.
  const activeByGoal: { [goal: string]: boolean } = {};
  workflows.forEach(w => {
    if (w.active) {
      if (activeByGoal[w.goal]) {
        w.active = false;
      } else {
        activeByGoal[w.goal] = true;
      }
    }
  });
  return workflows;
}